import * as d3 from 'd3';
import addAxisHover from '../../lib/addAxisHover';

export default {
  constructor() {
    this.width = null;
  },

  async create(
    el,
    timeSeriesData,
    {
      //primaryColour = '#6A88AA',
      //highlightColour = '#333',
      axisLabels = {},
      axisHoverText = {},
      suffix = null,
      performanceMode = 'means',
      minimalMode = false,
    },
  ) {
    // Chart dimensions
    const outerWidth = el.getBoundingClientRect().width;
    const margin = { top: 40, right: 30, bottom: 50, left: 50 };
    const height = minimalMode ? 120 : 300;

    const width = outerWidth - margin.left - margin.right;

    // Create SVG container
    const svg = d3
      .select(el)
      .attr('width', outerWidth)
      .attr('height', height + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${outerWidth} ${height + margin.top + margin.bottom}`);

    const chart = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Normalize input: extract all series into flat { label, values } format
    const seriesList = [];

    // --- Interval midpoint helper (works for hours, days, weeks, months, quarters) ---
    function inferDuration(bucket) {
      if (bucket === 'hour') return 1000 * 60 * 60;
      if (bucket === 'day') return 1000 * 60 * 60 * 24;
      if (bucket === 'week') return 1000 * 60 * 60 * 24 * 7;
      if (bucket === 'month') return 1000 * 60 * 60 * 24 * 30;
      if (bucket === 'quarter') return 1000 * 60 * 60 * 24 * 90;
      return 1000 * 60 * 60 * 24; // fallback
    }

    function normalizeWeekStart(date) {
      const d = new Date(date);
      const day = d.getDay(); // 0 = Sunday, 1 = Monday, ...
      const targetStart = 1; // choose Monday as week start
      const diff = (day - targetStart + 7) % 7;
      d.setDate(d.getDate() - diff);
      return d;
    }

    function detectBucket(scores) {
      if (!scores || scores.length < 2) return 'day';

      const first = new Date(scores[0].end).getTime();
      const second = new Date(scores[1].end).getTime();
      const diff = Math.abs(second - first);

      const oneDay = 1000 * 60 * 60 * 24;

      if (diff < oneDay * 1.5) return 'day';
      if (diff < oneDay * 10) return 'week';
      if (diff < oneDay * 40) return 'month';
      if (diff < oneDay * 120) return 'quarter';

      return 'day';
    }

    timeSeriesData.forEach((series) => {
      const hasDrivers = Array.isArray(series.time_series_scores?.[0]?.drivers);
      if (hasDrivers) {
        const driverMap = new Map();

        series.time_series_scores.forEach((score) => {
          score.drivers.forEach((driverObj) => {
            const driverLabel = driverObj.driver;
            if (!driverMap.has(driverLabel)) {
              driverMap.set(driverLabel, []);
            }
            driverMap.get(driverLabel).push({
              date: new Date(score.end),
              value: +driverObj.coefficient,
            });
          });
        });

        for (const [label, values] of driverMap.entries()) {
          seriesList.push({ label, values });
        }
      } else {
        // TODO if unique_counts present, and meta.scorecutoff, use top-box
        const values = series.time_series_scores.map((d) => {
          let value;
          if (typeof d.compound_score === 'number') {
            value = d.compound_score;
          } else if (
            typeof d.compound_score === 'string' &&
            d.compound_score.trim() !== '' &&
            !isNaN(parseFloat(d.compound_score))
          ) {
            value = parseFloat(d.compound_score);
          } else if (
            d.filtered_coverage &&
            d.filtered_coverage.total != null &&
            d.filtered_coverage.denominator != null &&
            !isNaN(+d.filtered_coverage.total) &&
            !isNaN(+d.filtered_coverage.denominator) &&
            +d.filtered_coverage.denominator !== 0
          ) {
            value = (+d.filtered_coverage.total / +d.filtered_coverage.denominator) * 100;
          } else if (performanceMode === 'Volumes' && d.valid_size != null) {
            value = +d.valid_size;
          } else if (d.modified_mean && d.modified_mean != null) {
            value = +d.modified_mean;
          } else if (d.mean != null) {
            value = +d.mean;
          } else {
            value = 0;
          }

          const bucket = detectBucket(series.time_series_scores);

          // Raw start/end
          let start = d.start ? new Date(d.start) : null;
          let end = new Date(d.end);

          // WEEK: use normalized week start as the actual x-date
          if (bucket === 'week') {
            start = normalizeWeekStart(start ?? end);

            return {
              date: start, // <-- key change: use start, not midpoint
              value,
            };
          }

          // NON-WEEK: keep midpoint logic
          const inferredStart = start ? start.getTime() : end.getTime() - inferDuration(bucket);
          const mid = new Date((inferredStart + end.getTime()) / 2);

          return {
            date: mid,
            value,
          };
        });
        if (series.segment_value && series.time_series_scores) {
          seriesList.push({
            label: `${series.segment_value} (${series.label})`,
            values,
          });
        } else {
          seriesList.push({ label: series.label, values });
        }
        // --- Dual Axis Sentiment Injection (minimalMode + single theme only) ---
        if (minimalMode && timeSeriesData.length === 1) {
          const sentimentValues = [];

          series.time_series_scores.forEach((d) => {
            const v = d._custom?.value || d;
            if (
              v.filtered_coverage.sentiment &&
              typeof v.filtered_coverage.sentiment.compound === 'number'
            ) {
              sentimentValues.push({
                date: new Date(v.end),
                value: v.filtered_coverage.sentiment.compound,
              });
            }
          });

          if (sentimentValues.length > 0) {
            seriesList.push({
              label: `${series.label} — Sentiment`,
              values: sentimentValues,
              axis: 'sentiment',
            });
          }
        }
      }
    });

    const allDates = seriesList.flatMap((s) => s.values.map((d) => d.date));
    const allValues = seriesList.flatMap((s) => s.values.map((d) => d.value));

    const x = d3.scaleTime().domain(d3.extent(allDates)).range([0, width]);

    const rawMin = d3.min(allValues);
    const rawMax = d3.max(allValues);

    // If everything is above zero, add a buffer — but never go below zero.
    let minY;
    if (rawMin >= 0) {
      const buffer = (rawMax - rawMin) * 0.1 || rawMin * 0.1 || 1;
      const buffered = rawMin - buffer;
      minY = buffered < 0 ? 0 : buffered; // clamp to zero
    } else {
      minY = rawMin; // preserve negative values
    }

    const maxY = rawMax;

    const y = d3.scaleLinear().domain([minY, maxY]).range([height, 0]).nice();

    const colorScale = d3
      .scaleOrdinal()
      .domain(seriesList.map((s) => s.label))
      .range(d3.schemeTableau10);

    const safeClassName = (label) => label.replace(/[^a-zA-Z0-9_-]/g, '_');

    const uniqueLabels = Array.from(new Map(seriesList.map((s) => [s.label, s])).values());

    let selectedSeries = new Set();

    // --- Dual-axis: detect sentiment series BEFORE drawing lines ---
    const hasSentiment = seriesList.some((s) => s.axis === 'sentiment');

    let ySentiment = null;
    if (hasSentiment) {
      ySentiment = d3.scaleLinear().domain([-1, 1]).range([height, 0]).nice();
    }

    // Draw each line and its points/labels
    uniqueLabels.forEach((series) => {
      const safeLabel = safeClassName(series.label);

      const yScale = series.axis === 'sentiment' && ySentiment ? ySentiment : y;

      const line = d3
        .line()
        .x((d) => x(d.date))
        .y((d) => yScale(d.value))
        .curve(d3.curveMonotoneX);

      const lineStrokeWidth = minimalMode ? 4 : 2.5;
      // --- LINE (click toggles labels) ---
      chart
        .append('path')
        .datum(series.values)
        .attr('class', `chart-line ${safeLabel}`)
        .attr('fill', 'none')
        .attr('stroke', colorScale(series.label))
        .attr('stroke-width', lineStrokeWidth)
        .attr('d', line)
        .style('cursor', 'pointer')
        .on('click', () => {
          if (selectedSeries.has(safeLabel)) {
            // Turn OFF this label group
            selectedSeries.delete(safeLabel);
            chart.select(`.label-group.${safeLabel}-labels`).style('display', 'none');
          } else {
            // Turn ON this label group
            selectedSeries.add(safeLabel);
            chart.select(`.label-group.${safeLabel}-labels`).style('display', null);
          }
        });

      if (series.axis === 'sentiment') {
        chart
          .select(`path.chart-line.${safeLabel}`)
          .attr('stroke', '#ff8c00')
          .attr('stroke-dasharray', '6 4');
      }

      // --- LABEL GROUP (hidden by default) ---
      const labelGroup = chart
        .append('g')
        .attr('class', `label-group ${safeLabel}-labels`)
        .style('display', 'none');

      // Draw circles (no safeLabel class)
      labelGroup
        .selectAll('circle')
        .data(series.values)
        .enter()
        .append('circle')
        .attr('class', 'label-circle')
        .attr('cx', (d) => x(d.date))
        .attr('cy', (d) => yScale(d.value))
        .attr('r', 3)
        .attr('fill', colorScale(series.label));

      // Draw labels (no safeLabel class)
      labelGroup
        .selectAll('text')
        .data(series.values)
        .enter()
        .append('text')
        .attr('class', 'chart__label')
        .attr('text-anchor', 'middle')
        .attr('x', (d) => x(d.date))
        .attr('y', (d) => yScale(d.value) - 10)
        .text((d) => d.value.toFixed(2) + (suffix || ''));
    });

    // X Axis
    const tickDates = uniqueLabels[0].values.map((v) => v.date);

    chart
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).tickValues(tickDates).tickFormat(d3.timeFormat('%b %d')));

    // Y Axis
    chart.append('g').call(d3.axisLeft(y).ticks(5));

    if (hasSentiment && ySentiment) {
      chart
        .append('g')
        .attr('transform', `translate(${width},0)`)
        .call(d3.axisRight(ySentiment).ticks(5));
    }

    // Axis Labels
    let xAxisHover = null;
    if (!minimalMode && axisLabels.x) {
      chart
        .append('text')
        .attr('class', 'chart__axis-label')
        .attr('x', width / 2)
        .attr('y', height + margin.bottom - 5)
        .attr('text-anchor', 'middle')
        .text(axisLabels.x)
        .on('mouseover', () => {
          const bbox = xAxisHover.node().getBBox();

          xAxisHover
            .attr(
              'transform',
              'translate(' + (width / 2 - bbox.width / 2) + ', ' + (height - bbox.height) + ')',
            )
            .attr('visibility', 'visible');
        })
        .on('mouseout', () => {
          xAxisHover.attr('visibility', 'hidden');
        });

      if (axisHoverText.x) {
        xAxisHover = addAxisHover(chart, axisHoverText.x, {
          x: 20,
          y: height / 2,
        });
      }
    }
    let yAxisHover = null;
    if (!minimalMode && axisLabels.y) {
      chart
        .append('text')
        .attr('class', 'chart__axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('x', -height / 2)
        .attr('y', -margin.left + 15)
        .attr('text-anchor', 'middle')
        .text(axisLabels.y)
        .on('mouseover', () => {
          yAxisHover.attr('visibility', 'visible');
        })
        .on('mouseout', () => {
          yAxisHover.attr('visibility', 'hidden');
        });

      let hover_y_label = '';
      if (!axisHoverText.y) {
        hover_y_label = '';
      } else {
        hover_y_label = axisHoverText.y;
      }
      yAxisHover = addAxisHover(chart, hover_y_label, {
        x: 20,
        y: height / 2,
      });
    }

    // Add zero line if needed
    if (minY < 0) {
      chart
        .append('line')
        .attr('class', 'zero-line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', '#999')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4 2');
    }

    // Hybrid Legend Rendering: wrap + truncate + tooltip
    if (!minimalMode) {
      const legend = svg
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top - 25})`);

      let xOffset = 0;
      let yOffset = 0;
      const rowHeight = 20;
      const padding = 10;

      uniqueLabels.forEach((series) => {
        const safeLabel = safeClassName(series.label);

        const legendRow = legend
          .append('g')
          .style('cursor', 'pointer')
          .on('click', () => {
            const isVisible = chart.selectAll(`.${safeLabel}`).style('display') !== 'none';
            chart.selectAll(`.${safeLabel}`).style('display', isVisible ? 'none' : null);
          });

        legendRow
          .append('rect')
          .attr('width', 10)
          .attr('height', 10)
          .attr('fill', colorScale(series.label));

        const displayLabel =
          series.label.length > 20 ? series.label.slice(0, 20) + '…' : series.label;

        legendRow
          .append('text')
          .attr('x', 15)
          .attr('y', 10)
          .text(displayLabel)
          .attr('font-size', '12px')
          .attr('alignment-baseline', 'middle')
          .append('title')
          .text(series.label);

        const actualWidth = legendRow.node().getBBox().width;

        if (xOffset + actualWidth > width - margin.right) {
          xOffset = 0;
          yOffset += rowHeight;
        }

        legendRow.attr('transform', `translate(${xOffset}, ${yOffset})`);
        xOffset += actualWidth + padding;
      });
    }
  },
};
